import React, { useEffect, useState } from 'react';
import { TabType } from 'types/TabTypes';
import { useTranslation } from 'react-i18next';
import Timeline from 'components/timeline/Timeline';
import PdfLayoutExample from '../../views/settings/pdf/PdfLayoutExample';
import JobDetailList from '../../views/jobs/JobDetailList';
import AttachmentDetailList from '../../views/attachment/AttachmentDetailList';
import ProposalDetailList from '../../views/proposals/ProposalDetailList';
import ProjectTimeRowTab from '../../views/jobs/ProjectTimeRowTab';
import { EditableStatusType } from 'types/FieldTypes';
import ProjectPlanningTab from '../../views/jobs/ProjectPlanningTab';
import ProjectJobsTab from '../../views/jobs/ProjectJobsTab';
import ProjectPhotosTab from '../../views/jobs/ProjectPhotosTab';
import ContractJobSchedulesTab from '../../views/contracts/ContractJobSchedulesTab';
import InvoiceDetailList from '../../views/invoices/InvoiceDetailList';

interface TabBarProps {
    tabs: TabType[];
    data?: any;
    viewKey?: string;
    itemId?: string;
    pageContext?: string;
    reloadTabs?: number;
    editable?: EditableStatusType;
    extraProps?: Record<string, any>;
}

export const TabBar = ({ 
    tabs, 
    data, 
    viewKey, 
    itemId, 
    pageContext, 
    reloadTabs,
    editable,
    extraProps,
}: TabBarProps) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState(0);

    // Sets the first tab to active
    useEffect(() => {
        if (setActiveTab && typeof activeTab === 'undefined') {
            setActiveTab(0);
        }
    }, [setActiveTab, activeTab]);

    // Generates the tab components
    const tabComponents = tabs.map((tab, index) => {
        let component = tab.component;
        if (!component && tab.type && viewKey) {
            switch(tab.type) {
                case 'timeline':
                    component = itemId && pageContext ? 
                        <Timeline 
                            key={index} 
                            placement='page'
                            timeline_page={pageContext || ''}
                            timeline_page_id={parseInt(itemId)}
                            showOnlyTimelineUpdates={tab.showOnlyTimelineUpdates}  
                            data={data}
                        /> : null;
                    break;
                case 'proposals': 
                    component = itemId && pageContext ? <ProposalDetailList key={index} itemId={itemId} detailPage={pageContext} data={data} /> : null;
                    break;
                case 'jobs': 
                    component = itemId && pageContext ? 
                        <JobDetailList 
                            key={index} 
                            itemId={itemId} 
                            detailPage={pageContext} 
                            data={data} 
                        /> : null;
                    break;
                case 'invoices': 
                    component = itemId && pageContext ? 
                        <InvoiceDetailList 
                            key={index} 
                            itemId={itemId} 
                            detailPage={pageContext} 
                            data={data} 
                        /> : null;
                    break;
                case 'pdf-layout':
                    component = itemId ? <PdfLayoutExample itemId={itemId} reloadPdf={reloadTabs ? reloadTabs : null} /> : null;
                    break;
                case 'attachments':
                    component = itemId && pageContext ? <AttachmentDetailList key={index} detailPageId={itemId} detailPage={pageContext} /> : null;
                    break;
                case 'project-jobs':
                    component = itemId && data && data.jobs ? (
                        <ProjectJobsTab
                            key={index}
                            project={data}
                            isEditable={editable}
                        />
                    ) : null;
                    break;
                case 'project-planning':
                    component = itemId && data && data.jobs ? (
                        <ProjectPlanningTab
                            key={index}
                            viewKey={viewKey}
                            project={data}
                            isEditable={editable}
                            weekStartDate={extraProps?.schedulerWeekStart}
                        />
                    ) : null;
                    break;
                case 'project-time-rows':
                    component = itemId ? <ProjectTimeRowTab key={index} data={data} /> : null;
                    break;
                case 'project-photos':
                    component = itemId ? <ProjectPhotosTab key={index} itemId={itemId} /> : null;
                    break;
                case 'contract-job-schedules':
                    component = itemId ? <ContractJobSchedulesTab key={index} itemId={itemId} data={data} /> : null;
                    break;
                // Add other tab components here
            }
        }

        // Sets the tab label and set the active tab
        const label = (
            <li key={index} 
                onClick={() => setActiveTab(index)}
                className={index === activeTab ? "active" : ""}>
                {t(tab.label)}
            </li>
        );
    
        // Renders the label and the component
        return {
            label,
            component: index === activeTab ? component : null
        };
    });

    const tabLabel = tabComponents.map(tab => tab.label);
    const tabComponent = tabComponents.map(tab => tab.component);

    return { tabLabel, tabComponent };
};